import Phaser from 'phaser';
import lottie from 'lottie-web';

import {
	getLottieLayerTextureTransform,
	getLottieMotionOffset,
} from '@/features/letterParty/game/utils/letterMovement';

import type { AnimationItem } from 'lottie-web';

export const LOTTIE_TEXTURE_SCALE = 1;

interface LottieLayerFollowerConfig {
	animationData: any;
	layerName: string;
}

export interface LottieCanvasLabelConfig {
	text: string;
	offsetY: number;
	size: number;
	depth: number;
	followLayer?: LottieLayerFollowerConfig;
}

interface LottieCanvasLabel {
	object: Phaser.GameObjects.Text;
	config: LottieCanvasLabelConfig;
}

interface LottieCanvasObject {
	animation: AnimationItem;
	baseX: number;
	baseY: number;
	container: HTMLDivElement;
	image: Phaser.GameObjects.Image | null;
	label: LottieCanvasLabel | null;
	motionAmplitude: number;
	motionPhase: number;
	originX: number;
	originY: number;
	signature: string;
	texture: Phaser.Textures.CanvasTexture | null;
	textureKey: string | null;
	width: number;
	height: number;
}

interface LottieCanvasConfig {
	key: string;
	signature: string;
	x: number;
	y: number;
	width: number;
	height: number;
	animationData: unknown;
	depth: number;
	offsetX?: number;
	offsetY?: number;
	isFlipped?: boolean;
	isPlaying: boolean;
	playbackSpeed?: number;
	preserveAspectRatio?: string;
	originX?: number;
	originY?: number;
	label?: LottieCanvasLabelConfig | null;
	motionAmplitude?: number;
	motionPhase?: number;
}

export default class LottieCanvas {
	private activeLottieKeys = new Set<string>();
	private lottieObjects = new Map<string, LottieCanvasObject>();
	private sourceRoot: HTMLDivElement | null = null;

	constructor(private readonly scene: Phaser.Scene, private readonly labelColor: string) {}

	public beginFrame() {
		this.activeLottieKeys = new Set();
	}

	public retain(keys: Iterable<string>) {
		for (const key of keys) {
			this.activeLottieKeys.add(key);
		}
	}

	public getActiveKeys() {
		return new Set(this.activeLottieKeys);
	}

	public update() {
		this.lottieObjects.forEach((lottieObject) => {
			const motionOffset = getLottieMotionOffset({
				currentFrame: lottieObject.animation.currentFrame,
				totalFrames: lottieObject.animation.totalFrames,
				motionAmplitude: lottieObject.motionAmplitude,
				motionPhase: lottieObject.motionPhase,
			});

			lottieObject.image?.setPosition(lottieObject.baseX, lottieObject.baseY + motionOffset);

			if (lottieObject.label) {
				const labelTransform = this.getLottieLabelTransform(lottieObject, motionOffset);

				lottieObject.label.object
					.setPosition(labelTransform.x, labelTransform.y)
					.setAngle(labelTransform.rotation);
			}

			lottieObject.texture?.refresh();
		});
	}

	public add({
		key,
		signature,
		x,
		y,
		width,
		height,
		animationData,
		depth,
		offsetY = 0,
		offsetX = 0,
		isFlipped = false,
		isPlaying,
		playbackSpeed = 1,
		preserveAspectRatio = 'xMidYMid meet',
		originX = 0.5,
		originY = 0.5,
		label = null,
		motionAmplitude = 0,
		motionPhase = 0,
	}: LottieCanvasConfig) {
		this.activeLottieKeys.add(key);

		const targetX = x + offsetX;
		const targetY = y + offsetY;
		const existing = this.lottieObjects.get(key);

		if (existing?.signature === signature) {
			existing.animation.setSpeed(playbackSpeed);
			this.updateLottieContainer(existing.container, width, height);
			existing.animation.resize();
			existing.baseX = targetX;
			existing.baseY = targetY;
			existing.height = height;
			existing.motionAmplitude = motionAmplitude;
			existing.motionPhase = motionPhase;
			existing.originX = originX;
			existing.originY = originY;
			existing.width = width;
			this.setAnimationPlayback(existing.animation, isPlaying);
			existing.image?.setDepth(depth);
			existing.image?.setPosition(targetX, targetY);
			existing.image?.setOrigin(originX, originY);
			existing.image?.setDisplaySize(width, height);
			existing.image?.setFlipX(isFlipped);
			this.updateLottieLabel(existing, label);
			return;
		}

		if (existing) {
			this.destroyLottieObject(existing);
		}

		const container = document.createElement('div');
		this.updateLottieContainer(container, width, height);
		this.getSourceRoot().appendChild(container);

		const animation = lottie.loadAnimation({
			container,
			renderer: 'canvas',
			loop: true,
			autoplay: false,
			animationData,
			rendererSettings: {
				clearCanvas: true,
				dpr: LOTTIE_TEXTURE_SCALE,
				preserveAspectRatio,
			},
		});
		animation.setSpeed(playbackSpeed);

		const lottieObject: LottieCanvasObject = {
			animation,
			baseX: targetX,
			baseY: targetY,
			container,
			image: null,
			label: null,
			motionAmplitude,
			motionPhase,
			originX,
			originY,
			signature,
			texture: null,
			textureKey: null,
			width,
			height,
		};

		this.updateLottieLabel(lottieObject, label);

		const attachTexture = () => {
			const canvas = container.querySelector('canvas');

			if (!(canvas instanceof HTMLCanvasElement) || lottieObject.image) {
				return;
			}

			const textureKey = `letter-party-lottie-${key}`;
			const texture = this.scene.textures.addCanvas(textureKey, canvas) as Phaser.Textures.CanvasTexture;
			const image = this.scene.add
				.image(targetX, targetY, textureKey)
				.setOrigin(originX, originY)
				.setDisplaySize(width, height)
				.setDepth(depth)
				.setFlipX(isFlipped);

			lottieObject.image = image;
			lottieObject.texture = texture;
			lottieObject.textureKey = textureKey;
			texture.refresh();
		};

		attachTexture();

		animation.addEventListener('DOMLoaded', () => {
			attachTexture();
			this.setAnimationPlayback(animation, isPlaying);
			lottieObject.texture?.refresh();
		});

		this.lottieObjects.set(key, lottieObject);
		this.setAnimationPlayback(animation, isPlaying);
	}

	public removeInactive() {
		this.lottieObjects.forEach((lottieObject, key) => {
			if (this.activeLottieKeys.has(key)) {
				return;
			}

			this.destroyLottieObject(lottieObject);
			this.lottieObjects.delete(key);
		});
	}

	public clear() {
		this.lottieObjects.forEach((lottieObject) => {
			this.destroyLottieObject(lottieObject);
		});
		this.lottieObjects.clear();
		this.sourceRoot?.remove();
		this.sourceRoot = null;
	}

	private updateLottieLabel(lottieObject: LottieCanvasObject, labelConfig: LottieCanvasLabelConfig | null) {
		if (!labelConfig) {
			lottieObject.label?.object.destroy();
			lottieObject.label = null;
			return;
		}

		if (!lottieObject.label) {
			const label = this.scene.add
				.text(lottieObject.baseX, lottieObject.baseY + labelConfig.offsetY, labelConfig.text, {
					fontFamily: 'ModernEra, Helvetica, Arial, sans-serif',
					fontSize: `${Math.round(labelConfig.size * 0.24)}px`,
					fontStyle: '900',
					color: this.labelColor,
					align: 'center',
					resolution: LOTTIE_TEXTURE_SCALE,
				})
				.setOrigin(0.5)
				.setDepth(labelConfig.depth);

			lottieObject.label = {
				object: label,
				config: labelConfig,
			};
			const labelTransform = this.getLottieLabelTransform(lottieObject, 0);

			label.setPosition(labelTransform.x, labelTransform.y).setAngle(labelTransform.rotation);
			return;
		}

		lottieObject.label.config = labelConfig;
		lottieObject.label.object
			.setText(labelConfig.text)
			.setFontSize(Math.round(labelConfig.size * 0.24))
			.setDepth(labelConfig.depth);
		const labelTransform = this.getLottieLabelTransform(lottieObject, 0);

		lottieObject.label.object.setPosition(labelTransform.x, labelTransform.y).setAngle(labelTransform.rotation);
	}

	private getLottieLabelTransform(lottieObject: LottieCanvasObject, motionOffset: number) {
		const labelConfig = lottieObject.label?.config;

		if (!labelConfig?.followLayer) {
			return {
				x: lottieObject.baseX,
				y: lottieObject.baseY + (labelConfig?.offsetY ?? 0) + motionOffset,
				rotation: 0,
			};
		}

		const animationData = labelConfig.followLayer.animationData;
		const layerTransform = getLottieLayerTextureTransform({
			animationData,
			layerName: labelConfig.followLayer.layerName,
			frame: lottieObject.animation.currentFrame,
			textureFrame: {
				baseX: lottieObject.baseX,
				baseY: lottieObject.baseY,
				width: lottieObject.width,
				height: lottieObject.height,
				originX: lottieObject.originX,
				originY: lottieObject.originY,
			},
		});

		return {
			x: layerTransform.x,
			y: layerTransform.y + labelConfig.offsetY + motionOffset,
			rotation: layerTransform.rotation,
		};
	}

	private updateLottieContainer(container: HTMLElement, width: number, height: number) {
		container.style.width = `${width}px`;
		container.style.height = `${height}px`;
	}

	private getSourceRoot() {
		if (this.sourceRoot) {
			return this.sourceRoot;
		}

		const root = document.createElement('div');
		root.className = 'letter-party-game__lottie-source-root';
		root.setAttribute('aria-hidden', 'true');
		document.body.appendChild(root);
		this.sourceRoot = root;

		return root;
	}

	private setAnimationPlayback(animation: AnimationItem, isPlaying: boolean) {
		if (isPlaying) {
			animation.play();
			return;
		}

		animation.goToAndStop(0, true);
	}

	private destroyLottieObject({ animation, container, image, label, textureKey }: LottieCanvasObject) {
		animation.destroy();
		container.remove();
		image?.destroy();
		label?.object.destroy();
		if (textureKey) {
			this.scene.textures.remove(textureKey);
		}
	}
}
